import React, { useState } from 'react';
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Image, Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface StudioLogoProps {
  organizationId: string | null;
  studioLogo: string | null;
  setStudioLogo: (url: string | null) => void;
}

const StudioLogo: React.FC<StudioLogoProps> = ({ 
  organizationId, 
  studioLogo, 
  setStudioLogo 
}) => {
  const [logoLoading, setLogoLoading] = useState(false);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!organizationId) {
      console.error('Organization ID is missing');
      return;
    }

    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo file too large. Maximum size is 5MB.");
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please use JPEG, PNG, WebP or SVG.");
      return;
    }

    setLogoLoading(true);
    try {
      const fileName = `logo_${organizationId}_${Date.now()}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('studio-assets')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('studio-assets')
        .getPublicUrl(fileName);

      const logoUrl = publicUrlData.publicUrl;

      const { error: updateError } = await supabase
        .from('organizations')
        .update({
          logo_url: logoUrl
        })
        .eq('id', organizationId);

      if (updateError) throw updateError;

      setStudioLogo(logoUrl);
      toast.success("Hive logo updated successfully");
    } catch (error: any) {
      console.error("Error uploading logo:", error);
      toast.error("Failed to upload hive logo");
    } finally {
      setLogoLoading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!organizationId) {
      console.error('Organization ID is missing');
      return;
    }

    setLogoLoading(true);
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          logo_url: null
        })
        .eq('id', organizationId);

      if (error) throw error;

      setStudioLogo(null);
      toast.success("Hive logo removed successfully");
    } catch (error: any) {
      console.error("Error removing logo:", error);
      toast.error("Failed to remove hive logo");
    } finally {
      setLogoLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hive Logo</CardTitle>
        <CardDescription>
          Upload your dance hive logo for branding
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 border rounded-md flex items-center justify-center bg-gray-50">
            {studioLogo ? (
              <img
                src={studioLogo}
                alt="Hive Logo"
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <Image className="h-8 w-8 text-gray-300" />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="logo-upload" className="block">
              {studioLogo ? "Change logo" : "Upload logo"}
            </Label>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="relative"
                disabled={logoLoading}
              >
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={handleLogoUpload}
                  disabled={logoLoading}
                />
                <Upload className="h-4 w-4 mr-2" />
                {logoLoading ? "Uploading..." : "Upload"}
              </Button>
              {studioLogo && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleRemoveLogo}
                  disabled={logoLoading}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Remove
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Recommended: Square image, max 5MB, JPEG/PNG/WebP/SVG.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StudioLogo;
